import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import moment from 'moment';

import {
  TextField,
  PhoneField,
  DateTimePickerField,
  MaterialSelectField,
} from 'components/FormFields';
import { states as stateslist, countriesCode, organizationTypes } from 'constants';

import i18n from 'i18n-js';
import * as Yup from 'yup';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import { BaseModal } from 'components/UI';
import { Divider } from '@material-ui/core';
import { Formik, Form, Field } from 'formik';
import { usersActions } from 'state/modules/users';
import { diffObjects, zip_by_country } from 'utils';
import { UserGroupsSelect } from 'components/AsyncFields';

const CustomerModal = ({ id, user, open, handleClick }) => {
  const formRef = useRef(null);
  const dispatch = useDispatch();
  const [states, setStates] = useState({});
  const [dirty, setDirty] = useState(false);
  const [usaStates, setUsaStates] = useState([]);
  const [statesAddress, setStatesAddress] = useState([]);
  const [statesLicense, setStatesLicense] = useState([]);
  const { fetchedUserSuccess, fetchingUser, errors } = useSelector((state) => state.users);
  const { localeFormatLower } = useSelector((state) => state.organizations);
  const { extra_fields } = useSelector((state) => state.config.settings?.core?.user || {});

  const userValidations = {
    user_group_id: id ? Yup.string() : Yup.string().required(i18n.t('common.required_field')),
    email: Yup.string()
      .trim()
      .email(i18n.t('common.invalid_email'))
      .required(i18n.t('common.required_field')),
    first_name: Yup.string().trim().required(i18n.t('common.required_field')),
    middle_name: Yup.string(),
    last_name: Yup.string().trim().required(i18n.t('common.required_field')),
    date_of_birth: Yup.string(),
    mobile_phone: Yup.string(),
    internal_id: Yup.string().max(25, i18n.t('common.max_value', { max_value: 25 })),
    driver_license: Yup.object().shape({
      license_id: Yup.string(),
      license_expiration: Yup.string(),
      license_country: Yup.string(),
      license_state: Yup.string(),
      jurisdiction_vehicle_class: Yup.string().nullable(),
    }),
    address: Yup.object().shape({
      country: Yup.string(),
      state: Yup.string(),
      city: Yup.string(),
      street_address_line_1: Yup.string(),
      street_address_line_2: Yup.string(),
      zip_code: zip_by_country(),
    }),
  };

  // Validation schema of update user form
  const [updateUserSchema, setUpdateUserSchema] = useState(Yup.object().shape(userValidations));

  const initialData = {
    email: user?.email || '',
    user_group_id: undefined,
    last_name: user?.last_name || '',
    first_name: user?.first_name || '',
    middle_name: user?.middle_name || '',
    mobile_phone: user?.mobile_phone || '',
    date_of_birth: user?.date_of_birth || '',
    driver_license: {
      license_id: user?.driver_license?.license_id || '',
      license_expiration: user?.driver_license?.license_expiration || '',
      license_country: user?.driver_license?.license_country || '',
      license_state: user?.driver_license?.license_state || '',
      jurisdiction_vehicle_class: user?.driver_license?.jurisdiction_vehicle_class || undefined,
    },
    address: {
      country: user?.address?.country || '',
      state: user?.address?.state || '',
      city: user?.address?.city || '',
      street_address_line_1: user?.address?.street_address_line_1 || '',
      street_address_line_2: user?.address?.street_address_line_2 || '',
      zip_code: user?.address?.zip_code || '',
    },
    internal_id: user?.internal_id || '',
  };

  // Initials values of the form
  const [initialValues, setInitialValues] = useState(initialData);

  useEffect(() => {
    // This will replace object keys of the state list
    // This is needed to handle the country code of three digits
    let states = { ...stateslist };
    delete Object.assign(states, {['can']: states['ca'] })['ca']; // eslint-disable-line
    delete Object.assign(states, {['usa']: states['us'] })['us']; // eslint-disable-line
    delete Object.assign(states, {['aus']: states['au'] })['au']; // eslint-disable-line
    delete Object.assign(states, {['mex']: states['mx'] })['mx']; // eslint-disable-line
    delete Object.assign(states, {['nzl']: states['nz'] })['nz']; // eslint-disable-line
    delete Object.assign(states, {['col']: states['co'] })['co']; // eslint-disable-line
    setStates(states);

    // Check if user has country and loads states
    if (user?.address?.country) {
      setStatesAddress(states[user?.address?.country.toLowerCase() || 'usa']);
    }
    if (user?.driver_license?.license_country) {
      setStatesLicense(states[user?.driver_license?.license_country.toLowerCase() || 'usa']);
    }
  }, [user]);

  useEffect(() => {
    const states = stateslist?.us?.map((data) => {
      return {
        value: data.label,
        label: data.label,
      };
    });
    setUsaStates(states);
  }, []);

  useEffect(() => {
    if (extra_fields && Object.keys(extra_fields || {})?.length) {
      const extraFieldsData = {};
      const data = { ...initialData };
      data.extra_fields = {};
      Object.keys(extra_fields).map((key) => {
        const type = extra_fields[key]?.type || 'string';

        if (extra_fields[key]?.required)
          extraFieldsData[key] = Yup[type]().trim().required(i18n.t('common.required_field'));
        else extraFieldsData[key] = Yup[type]().trim();

        data.extra_fields[key] = user?.extra_fields ? user?.extra_fields[key] : '';

        return true;
      });

      const validations = { ...userValidations };
      validations.extra_fields = Yup.object().shape(extraFieldsData);

      setInitialValues(data);
      setUpdateUserSchema(Yup.object().shape(validations));
    }
  }, [extra_fields]) // eslint-disable-line

  /**
   * Handle change state according to selected country
   * @param {object} e
   */
  const changeState = (e) => {
    if (e?.target?.value) {
      const country = e?.target?.value?.toLowerCase();
      if (e.target.name === 'driver_license.license_country') {
        setStatesLicense(states[country || 'usa']);
      } else {
        setStatesAddress(states[country || 'usa']);
      }
      onChange(e);
    }
  };

  useEffect(() => {
    dispatch(usersActions.resetUpdateUser());
  }, [dispatch]);

  useEffect(() => {
    if (fetchedUserSuccess) {
      handleClick(null, true);
      dispatch(usersActions.resetUpdateUser());
    }
  }, [fetchedUserSuccess, handleClick, dispatch]);

  const onChange = ({ target }, isDate = false) => {
    const prevValue = get(initialValues, target?.name);
    const isDirty = isDate
      ? !moment(target?.value).isSame(prevValue, 'day')
      : prevValue !== target?.value;
    setDirty(isDirty);
  };

  const onChangeDates = (name, value) => {
    onChange({ target: { name, value } }, true);
  };

  const fillPrevValues = (data, keys) => {
    keys.forEach((key) => {
      if (data?.[key]) {
        data[key] = { ...(initialValues?.[key] || {}), ...data?.[key] };
        Object.keys(data[key]).forEach((item) => {
          if (!data[key][item] && (item === 'country' || item === 'license_country')) {
            data[key][item] = null;
          }
        });
      }
    });
    return data;
  };

  /**
   * Handle form submit
   * @param {object} values Object with the value of fields of the form
   */
  const onSubmit = (values, { setErrors }) => {
    // Create payload based on dirty fields
    const payload = fillPrevValues(diffObjects(initialValues, values), [
      'address',
      'driver_license',
    ]);
    if (payload.email && id) {
      dispatch(usersActions.fetchSendConfirmationLink(id, payload.email));
    }

    const { mobile_phone, phone_code, ...valuesNoMobile } = payload;
    if (mobile_phone) {
      if (mobile_phone === `+${phone_code}`) {
        return setErrors({ mobile_phone: i18n.t('common.required_field') });
      }
      delete payload.phone_code;
      if (id) {
        dispatch(usersActions.fetchUpdateUser(id, payload));
      } else {
        dispatch(usersActions.fetchCreateUser({ ...payload, is_customer: true }));
      }
    } else {
      if (id) {
        dispatch(usersActions.fetchUpdateUser(id, valuesNoMobile));
      } else {
        dispatch(usersActions.fetchCreateUser({ ...valuesNoMobile, is_customer: true }));
      }
    }
  };

  const handleSubmit = () => {
    formRef.current.submitForm();
  };
  return (
    <BaseModal
      size="md"
      open={open}
      hasEnterButton
      hasCancelButton
      disabled={!dirty}
      onCancel={handleClick}
      onEnter={handleSubmit}
      handleClick={handleClick}
      enableCloseButton={false}
      loading={fetchingUser}
      enterButtonLabel={i18n.t(`common.${id ? 'update' : 'create'}`)}
      title={i18n.t(`customers.${id ? 'update_customer' : 'create_customer'}`)}
      data-cy="modal-update-customer"
    >
      <Formik
        innerRef={formRef}
        onSubmit={onSubmit}
        enableReinitialize={true}
        initialValues={initialValues}
        validationSchema={updateUserSchema}
      >
        {({ handleSubmit, values }) => (
          <Form noValidate onSubmit={handleSubmit} onChange={onChange} autoComplete="nope">
            <Grid container spacing={2}>
              {!id && (
                <Grid item xs={12} sm={6}>
                  <Box mt={1}>
                    <Field
                      margin="none"
                      id="user_group_id"
                      name="user_group_id"
                      required={true}
                      component={UserGroupsSelect}
                      label={i18n.t('users.user_group')}
                      customParams={{ user_group_type: ['DRIVER'] }}
                    />
                  </Box>
                </Grid>
              )}
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={6} sm={6}>
                <Box>
                  <Field
                    type="text"
                    margin="none"
                    name="first_name"
                    inputProperties={{}}
                    component={TextField}
                    asyncErrors={errors?.first_name}
                    label={i18n.t('fields.first_name')}
                  />
                </Box>
              </Grid>
              <Grid item xs={6} sm={6}>
                <Box>
                  <Field
                    type="text"
                    margin="none"
                    name="middle_name"
                    required={false}
                    inputProperties={{}}
                    component={TextField}
                    asyncErrors={errors?.middle_name}
                    label={i18n.t('fields.middle_name')}
                  />
                </Box>
              </Grid>
              <Grid item xs={6} sm={6}>
                <Box>
                  <Field
                    type="text"
                    margin="none"
                    name="last_name"
                    inputProperties={{}}
                    component={TextField}
                    asyncErrors={errors?.last_name}
                    label={i18n.t('fields.last_name')}
                  />
                </Box>
              </Grid>
              <Grid item xs={6} sm={6}>
                <Box>
                  <Field
                    utc={true}
                    editable={true}
                    name="date_of_birth"
                    maxDate={new Date()}
                    showTimeSelect={false}
                    required={false}
                    dateFormat={localeFormatLower}
                    popperPlacement="bottom-end"
                    margin="none"
                    handleChange={onChangeDates}
                    component={DateTimePickerField}
                    label={i18n.t('customers.birthdate')}
                    asyncErrors={errors?.date_of_birth}
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={6} sm={6}>
                <Box>
                  <Field
                    type="email"
                    name="email"
                    margin="none"
                    inputProperties={{}}
                    component={TextField}
                    asyncErrors={errors?.email}
                    label={i18n.t('fields.email')}
                  />
                </Box>
              </Grid>
              <Grid item xs={6} sm={6}>
                <Box>
                  <Field
                    margin="none"
                    required={false}
                    name="mobile_phone"
                    dialcodename="phone_code"
                    label={i18n.t('fields.phone')}
                    asyncErrors={errors?.mobile_phone}
                    component={user?.mobile_phone ? PhoneField : TextField}
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={6} sm={6}>
                <Box>
                  <Field
                    type="text"
                    margin="none"
                    inputProperties={{}}
                    component={TextField}
                    required={false}
                    name="driver_license.license_id"
                    label={i18n.t('customers.driver_license')}
                    asyncErrors={errors?.license_id}
                  />
                </Box>
              </Grid>
              <Grid item xs={6} sm={6}>
                <Box>
                  <Field
                    editable={true}
                    minDate={new Date()}
                    showTimeSelect={false}
                    dateFormat={localeFormatLower}
                    popperPlacement="bottom-end"
                    component={DateTimePickerField}
                    required={false}
                    margin="none"
                    handleChange={onChangeDates}
                    name="driver_license.license_expiration"
                    label={i18n.t('customers.license_expiration_date')}
                    asyncErrors={errors?.license_expiration}
                  />
                </Box>
              </Grid>
              <Grid item xs={6} sm={6}>
                <Box>
                  <Field
                    options={countriesCode}
                    handleChange={changeState}
                    component={MaterialSelectField}
                    name="driver_license.license_country"
                    placeholder={i18n.t('customers.license_country')}
                    asyncErrors={errors?.license_country}
                  />
                </Box>
              </Grid>
              {statesLicense && (
                <Grid item xs={6} sm={6}>
                  <Box>
                    <Field
                      options={statesLicense || []}
                      component={MaterialSelectField}
                      handleChange={(e) => onChange(e)}
                      name="driver_license.license_state"
                      placeholder={i18n.t('customers.license_state')}
                      readOnly={!values?.driver_license?.license_country}
                      asyncErrors={errors?.license_state}
                    />
                  </Box>
                </Grid>
              )}
              <Grid item xs={6} sm={6}>
                <Box>
                  <Field
                    type="text"
                    margin="none"
                    inputProperties={{}}
                    component={TextField}
                    required={false}
                    name="driver_license.jurisdiction_vehicle_class"
                    label={i18n.t('customers.jurisdiction')}
                    asyncErrors={errors?.driver_license?.jurisdiction_vehicle_class}
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={6} sm={6}>
                <Box>
                  <Field
                    options={countriesCode}
                    name="address.country"
                    required={false}
                    handleChange={changeState}
                    component={MaterialSelectField}
                    asyncErrors={errors?.country}
                    placeholder={i18n.t('common.country')}
                  />
                </Box>
              </Grid>
              {statesAddress && (
                <Grid item xs={6} sm={6}>
                  <Box>
                    <Field
                      name="address.state"
                      options={statesAddress || []}
                      component={MaterialSelectField}
                      handleChange={(e) => onChange(e)}
                      readOnly={!values?.address?.country}
                      asyncErrors={errors?.state}
                      placeholder={i18n.t('common.state')}
                    />
                  </Box>
                </Grid>
              )}
              <Grid item xs={6} sm={6}>
                <Box>
                  <Field
                    type="text"
                    margin="none"
                    required={false}
                    name="address.city"
                    inputProperties={{}}
                    component={TextField}
                    label={i18n.t('common.city')}
                    asyncErrors={errors?.address?.city}
                  />
                </Box>
              </Grid>
              <Grid item xs={6} sm={6}>
                <Box>
                  <Field
                    type="text"
                    margin="none"
                    required={false}
                    inputProperties={{}}
                    component={TextField}
                    label={i18n.t('common.address')}
                    name="address.street_address_line_1"
                    asyncErrors={errors?.address?.street_address_line_1}
                  />
                </Box>
              </Grid>
              <Grid item xs={6} sm={6}>
                <Box>
                  <Field
                    type="text"
                    margin="none"
                    required={false}
                    inputProperties={{}}
                    component={TextField}
                    label={i18n.t('common.address_2')}
                    name="address.street_address_line_2"
                    asyncErrors={errors?.address?.street_address_line_2}
                  />
                </Box>
              </Grid>
              <Grid item xs={6} sm={6}>
                <Box>
                  <Field
                    type="text"
                    margin="none"
                    required={false}
                    inputProperties={{}}
                    component={TextField}
                    name="address.zip_code"
                    label={i18n.t('common.zip')}
                    asyncErrors={errors?.zip_code}
                  />
                </Box>
              </Grid>
              <Grid item xs={6} sm={6}>
                <Box>
                  <Field
                    type="text"
                    margin="none"
                    required={false}
                    inputProperties={{}}
                    component={TextField}
                    name="internal_id"
                    label={i18n.t('customers.internal_id')}
                    asyncErrors={errors?.internal_id}
                    data-cy="customers-internal-id"
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              {/* Business Information */}
              {extra_fields &&
                Object.keys(extra_fields).map((key) => {
                  return (
                    <Grid item xs={6} sm={6} key={key}>
                      <Box>
                        {['organization_state', 'organization_type'].includes(key) ? (
                          <Field
                            options={key === 'organization_state' ? usaStates : organizationTypes}
                            handleChange={(e) => onChange(e)}
                            component={MaterialSelectField}
                            name={`extra_fields.${key}`}
                            margin="none"
                            placeholder={i18n.t(`customers.organization.${key}`)}
                            asyncErrors={errors?.extra_fields?.[key]}
                          />
                        ) : (
                          <Field
                            type="text"
                            margin="none"
                            required={extra_fields[key].required}
                            name={`extra_fields.${key}`}
                            inputProperties={{}}
                            component={TextField}
                            label={i18n.t(`customers.${key}`)}
                            asyncErrors={errors?.extra_fields?.[key]}
                          />
                        )}
                      </Box>
                    </Grid>
                  );
                })}
            </Grid>
          </Form>
        )}
      </Formik>
    </BaseModal>
  );
};

CustomerModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClick: PropTypes.func.isRequired,
};

CustomerModal.defaultProps = {
  open: false,
  handleClick: () => {},
};

export default CustomerModal;
